// Iter-196 — "Encaissements à venir" sidebar widget.
// Pairs with AutoInvoiceForecastWidget on Invoices + Dashboard:
//   • Highlights the total expected to land in the next N days (default 30).
//   • Lists each pending sent invoice with client + due date.
//   • Surfaces a separate "overdue" block at the top so the CEO can act fast.
//
// Loads from GET /api/invoices/upcoming-receivables?days=30.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, AlertTriangle, Clock, Loader2 } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 });

// Iter-206x — tooltip text exposing the original currency + FX rate applied.
function tooltipFx(it) {
  if (!it || !it.currency || it.currency === "MAD") return "";
  const orig = Number(it.amount_original || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
  const mad = Number(it.amount_mad || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 });
  return `${orig} ${it.currency} × ${it.fx_rate_to_mad || 1} = ${mad} MAD`;
}

function formatDueRelative(due, today, daysUntil) {
  const d = new Date(due + "T00:00:00");
  const fmtDate = d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  if (daysUntil < 0) return `${fmtDate} · J${daysUntil}`;
  if (daysUntil === 0) return `${fmtDate} · aujourd'hui`;
  return `${fmtDate} · J+${daysUntil}`;
}

export default function UpcomingReceivablesWidget({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/invoices/upcoming-receivables", { params: { days } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Erreur chargement encaissements");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading && !data) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-5 text-sm text-zinc-500" data-testid="receivables-widget-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement encaissements…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 flex flex-col gap-4" data-testid="receivables-widget">
      <div>
        <h2 className="font-heading font-bold text-lg text-zinc-900 flex items-center gap-2">
          <Banknote size={16} /> Encaissements à venir
        </h2>
        <div className="text-xs text-zinc-500 mt-1">
          Factures envoyées non payées, dues dans les {data.days} prochains jours.
        </div>
      </div>

      {/* Overdue block — only when there are past-due invoices */}
      {data.overdue_count > 0 && (
        <div
          className="rounded-md border border-red-200 bg-red-50 p-3"
          data-testid="receivables-overdue-block"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-red-900 font-semibold">
              <AlertTriangle size={12} /> En retard
            </div>
            <div className="text-[10px] font-mono text-red-700">
              {data.overdue_count} facture{data.overdue_count > 1 ? "s" : ""}
            </div>
          </div>
          <div className="text-2xl font-mono font-bold text-red-900" data-testid="receivables-overdue-total">
            {fmt(data.overdue_total_mad)} <span className="text-sm font-normal">MAD</span>
          </div>
          <ul className="mt-2 space-y-1">
            {data.overdue_items.slice(0, 4).map((it) => (
              <li
                key={it.invoice_id}
                className="flex items-center justify-between text-[11px] font-mono text-red-900 border-t border-red-100 pt-1 first:border-0 first:pt-0"
                data-testid={`receivables-overdue-item-${it.invoice_id}`}
                title={tooltipFx(it)}
              >
                <span className="truncate flex-1 mr-2">
                  <span className="text-zinc-700">{it.client_name || "—"}</span>
                  <span className="text-red-700"> · {formatDueRelative(it.due_date, data.today, it.days_until_due)}</span>
                  {it.currency && it.currency !== "MAD" && (
                    <span className="ml-1 px-1 rounded bg-red-100 text-red-700 text-[9px] uppercase">{it.currency}</span>
                  )}
                </span>
                <span className="font-semibold shrink-0">{fmt(it.amount_mad)}</span>
              </li>
            ))}
            {data.overdue_count > 4 && (
              <li className="text-[10px] italic text-red-700 text-center pt-1">+{data.overdue_count - 4} autre(s)…</li>
            )}
          </ul>
        </div>
      )}

      {/* Upcoming block */}
      <div data-testid="receivables-upcoming-block">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-zinc-500 font-semibold">
            <Clock size={12} /> Dans {data.days} jours
          </div>
          <div className="text-[10px] font-mono text-zinc-500">
            {data.upcoming_count} facture{data.upcoming_count > 1 ? "s" : ""}
          </div>
        </div>
        <div className="text-2xl font-mono font-bold text-emerald-800" data-testid="receivables-upcoming-total">
          {fmt(data.upcoming_total_mad)} <span className="text-sm font-normal text-zinc-500">MAD</span>
        </div>
        {data.items.length === 0 ? (
          <div className="text-[11px] italic text-zinc-400 mt-2 border border-dashed border-zinc-200 rounded p-3 text-center">
            Aucune facture à recevoir dans cette fenêtre.
          </div>
        ) : (
          <ul className="mt-2 space-y-1">
            {data.items.slice(0, 6).map((it) => (
              <li
                key={it.invoice_id}
                className="flex items-center justify-between text-[11px] font-mono border-t border-zinc-100 pt-1 first:border-0 first:pt-0"
                data-testid={`receivables-upcoming-item-${it.invoice_id}`}
                title={tooltipFx(it)}
              >
                <span className="truncate flex-1 mr-2">
                  <span className="text-zinc-800">{it.client_name || "—"}</span>
                  <span className="text-zinc-500"> · {formatDueRelative(it.due_date, data.today, it.days_until_due)}</span>
                  {it.currency && it.currency !== "MAD" && (
                    <span className="ml-1 px-1 rounded bg-zinc-100 text-zinc-600 text-[9px] uppercase">{it.currency}</span>
                  )}
                </span>
                <span className="font-semibold shrink-0 text-zinc-900">{fmt(it.amount_mad)}</span>
              </li>
            ))}
            {data.upcoming_count > 6 && (
              <li className="text-[10px] italic text-zinc-500 text-center pt-1">+{data.upcoming_count - 6} autre(s)…</li>
            )}
          </ul>
        )}
      </div>

      <Link
        to="/invoices"
        data-testid="receivables-widget-link"
        className="text-[11px] text-zinc-600 hover:text-zinc-900 underline self-start"
      >
        Voir toutes les factures →
      </Link>

      {/* Iter-206x — FX rates used for conversion (transparency for the CEO). */}
      {data.fx_rates_used && Object.keys(data.fx_rates_used).filter((k) => k !== "MAD").length > 0 && (
        <div
          className="text-[10px] font-mono text-zinc-400 border-t border-zinc-100 pt-2 flex flex-wrap gap-x-3 gap-y-0.5"
          data-testid="receivables-fx-rates"
        >
          <span className="text-zinc-500">Taux MAD :</span>
          {Object.entries(data.fx_rates_used)
            .filter(([k]) => k !== "MAD")
            .map(([k, v]) => (
              <span key={k} className="text-zinc-500">
                1 {k} = <span className="text-zinc-700">{Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 4 })}</span>
              </span>
            ))}
        </div>
      )}
    </div>
  );
}
